import { ImpactType, ProjectType, ProjectSubType, PublicationStatus as PrismaProjectStatus } from '@prisma/client';
import type { ProjectSubTypeByProjectType, ProjectTypeByImpactType } from './admin-project-validation';

export type PublicationStatus = PrismaProjectStatus;

export const ADMIN_TREE_PROJECT_TYPES = [ProjectType.REFORESTATION] as const;
export type AdminTreeProjectType = (typeof ADMIN_TREE_PROJECT_TYPES)[number];

export const ADMIN_TREE_PROJECT_SUBTYPES = [
  ProjectSubType.GLOBAL_REFORESTATION,
  ProjectSubType.UK_REFORESTATION,
  ProjectSubType.US_REFORESTATION,
  ProjectSubType.BRAZIL_REFORESTATION,
  ProjectSubType.AUSTRALIA_REFORESTATION,
] as const;
export type AdminTreeProjectSubType = (typeof ADMIN_TREE_PROJECT_SUBTYPES)[number];

export const ADMIN_CARBON_AVOIDANCE_PROJECT_TYPES = [
  ProjectType.FORESTRY,
  ProjectType.CAPTURE,
  ProjectType.WETLANDS,
  ProjectType.RENEWABLES,
  ProjectType.COMMUNITY,
  ProjectType.TRANSPORT,
  ProjectType.GEOTHERMAL,
  ProjectType.HOUSEHOLD_RETROFIT,
] as const;
export type AdminCarbonAvoidanceProjectType = (typeof ADMIN_CARBON_AVOIDANCE_PROJECT_TYPES)[number];

export const ADMIN_CARBON_REMOVAL_PROJECT_TYPES = [
  ProjectType.AFFORESTATION_REFORESTATION_AND_REVEGETATION,
  ProjectType.BIOCHAR,
  ProjectType.BLUE_CARBON,
  ProjectType.DIRECT_AIR_CAPTURE,
  ProjectType.ENHANCED_ROCK_WEATHERING,
  ProjectType.REGENERATIVE_AGRICULTURE,
] as const;
export type AdminCarbonRemovalProjectType = (typeof ADMIN_CARBON_REMOVAL_PROJECT_TYPES)[number];

export const ADMIN_HABITAT_RESTORATION_PROJECT_TYPES = [
  ProjectType.WILDFLOWERS,
  ProjectType.SEAGRASS,
  ProjectType.PEATLANDS,
  ProjectType.RAINFOREST_PROTECTION,
  ProjectType.MARINE_PROTECTION_RESTORATION,
] as const;
export type AdminHabitatRestorationProjectType = (typeof ADMIN_HABITAT_RESTORATION_PROJECT_TYPES)[number];

export const ADMIN_PROJECT_TYPES = [
  ...ADMIN_TREE_PROJECT_TYPES,
  ...ADMIN_CARBON_AVOIDANCE_PROJECT_TYPES,
  ...ADMIN_CARBON_REMOVAL_PROJECT_TYPES,
  ...ADMIN_HABITAT_RESTORATION_PROJECT_TYPES,
] as const satisfies readonly ProjectType[];
export type AdminProjectType = (typeof ADMIN_PROJECT_TYPES)[number];

export const PROJECT_TYPES_BY_IMPACT_TYPE = {
  TREES: ADMIN_TREE_PROJECT_TYPES,
  CARBON_OFFSETS: ADMIN_CARBON_AVOIDANCE_PROJECT_TYPES,
  CARBON_REMOVAL: ADMIN_CARBON_REMOVAL_PROJECT_TYPES,
  HABITAT_RESTORATION: ADMIN_HABITAT_RESTORATION_PROJECT_TYPES,
} as const satisfies ProjectTypeByImpactType;

export const ADMIN_RENEWABLES_PROJECT_SUBTYPE = [
  ProjectSubType.BIOMASS,
  ProjectSubType.HYDRO,
  ProjectSubType.SOLAR,
  ProjectSubType.WIND,
] as const;
export type AdminRenewablesProjectSubType = (typeof ADMIN_RENEWABLES_PROJECT_SUBTYPE)[number];

export const ADMIN_FORESTRY_PROJECT_SUBTYPE = [ProjectSubType.FOREST_PROTECTION] as const;
export type AdminForestryProjectSubType = (typeof ADMIN_FORESTRY_PROJECT_SUBTYPE)[number];
export type AdminCarbonAvoidanceProjectSubType = AdminRenewablesProjectSubType | AdminForestryProjectSubType;

export const ADMIN_PROJECT_SUBTYPES = [
  ...ADMIN_TREE_PROJECT_SUBTYPES,
  ...ADMIN_RENEWABLES_PROJECT_SUBTYPE,
  ...ADMIN_FORESTRY_PROJECT_SUBTYPE,
] as const satisfies readonly ProjectSubType[];
export type AdminProjectSubType = (typeof ADMIN_PROJECT_SUBTYPES)[number];

export const PROJECT_SUBTYPES_BY_IMPACT_TYPE = {
  TREES: {
    [ProjectType.REFORESTATION]: ADMIN_TREE_PROJECT_SUBTYPES,
  },
  CARBON_OFFSETS: {
    [ProjectType.RENEWABLES]: ADMIN_RENEWABLES_PROJECT_SUBTYPE,
    [ProjectType.FORESTRY]: ADMIN_FORESTRY_PROJECT_SUBTYPE,
  },
  CARBON_REMOVAL: {},
  HABITAT_RESTORATION: {},
} as const satisfies Record<ImpactType, ProjectSubTypeByProjectType>;
